import type { LucideIcon } from 'lucide-react';
import {
  createContext,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useId,
} from 'react';

import { cn } from '../lib/cn';

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (context === null) {
    throw new Error(`<${component}> must be rendered inside <Tabs>`);
  }
  return context;
}

export interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

/**
 * A controlled, hand-rolled tabs primitive (WAI-ARIA APG automatic
 * activation pattern) \u2014 fits the tab-in-search-param usage `ContractDetailPage`/
 * `CustomerDetailPage` already have.
 */
export function Tabs({ value, onValueChange, children, className }: TabsProps): JSX.Element {
  const baseId = useId();

  return (
    <TabsContext.Provider value={{ value, onValueChange, baseId }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabListProps {
  'aria-label': string;
  children: ReactNode;
  className?: string;
}

export function TabList({ children, className, ...props }: TabListProps): JSX.Element {
  return (
    <div
      role="tablist"
      className={cn('flex gap-1 border-b border-slate-200', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface TabProps {
  value: string;
  children: ReactNode;
  /** Decorative: aria-hidden. Does not change the tab's accessible name. */
  icon?: LucideIcon;
  className?: string;
}

export function Tab({ value, children, icon: Icon, className }: TabProps): JSX.Element {
  const { value: activeValue, onValueChange, baseId } = useTabsContext('Tab');
  const selected = value === activeValue;

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const tablist = event.currentTarget.closest('[role="tablist"]');
    if (tablist === null) return;

    const tabs = Array.from(tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    const currentIndex = tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextTab = tabs[(currentIndex + delta + tabs.length) % tabs.length];
    if (nextTab === undefined) return;

    const nextValue = nextTab.dataset.value;
    if (nextValue === undefined) return;

    event.preventDefault();
    nextTab.focus();
    onValueChange(nextValue);
  }

  return (
    <button
      type="button"
      role="tab"
      id={tabId(baseId, value)}
      aria-controls={panelId(baseId, value)}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      data-value={value}
      onClick={() => onValueChange(value)}
      onKeyDown={handleKeyDown}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-200',
        selected
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
        className,
      )}
    >
      {Icon !== undefined ? <Icon aria-hidden="true" className="size-4" /> : null}
      {children}
    </button>
  );
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

/** Mounts its content only while `value === activeValue` \u2014 same mount count as today's `{tab === 'x' ? <X /> : null}`. */
export function TabPanel({ value, children, className }: TabPanelProps): JSX.Element | null {
  const { value: activeValue, baseId } = useTabsContext('TabPanel');
  if (value !== activeValue) return null;

  return (
    <div
      id={panelId(baseId, value)}
      role="tabpanel"
      aria-labelledby={tabId(baseId, value)}
      tabIndex={0}
      className={cn('pt-6', className)}
    >
      {children}
    </div>
  );
}

function tabId(baseId: string, value: string): string {
  return `${baseId}-tab-${value}`;
}

function panelId(baseId: string, value: string): string {
  return `${baseId}-panel-${value}`;
}
